import React from 'react';
import { useViewModel } from './useViewModel';

import { PersonalisationHelperModal } from '../PersonalisationHelperModal';

import styles from './personalisation-summary-modal.module.css';

/**
 * PersonalisationSummaryModal component displays a summary of all user selections in the personalisation journey.
 * Shows all available slots - selected items display their values and edit buttons,
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.isOpen=false] - Whether the modal is open
 * @param {function} [props.onClose=()=>{}] - Callback for closing the modal
 * @returns {import('react').JSX.Element}
 */
export default function PersonalisationSummaryModal({ isOpen = false, onClose = () => {} }) {
  const { productTitle, summaryItems, currentTotalPrice, handleEditClick, handleRemoveSelection, formatPrice, sharedPersonalisationText } =
    useViewModel({ onClose });

  return (
    <PersonalisationHelperModal
      isOpen={isOpen}
      onClose={onClose}
      title={productTitle}
      subtitle={sharedPersonalisationText}
      noContentPaddingBottom={true}
    >
      <div className={styles['personalisation__summary']}>
        <ul role="list" className={styles['personalisation__summary-list']}>
          {summaryItems.map((item) => {
            if (item.slotType === 'PAGE_CONTENT') {
              return null;
            }
            return (
              <li
                className={`${styles['personalisation__summary-item']} ${item.isSelected ? '' : styles['personalisation__summary-item--disabled']}`}
                key={item.id}
              >
                <div className={styles['personalisation__summary-item-upper']}>
                  <p className="body-sm">{item.title}:</p>
                  <div className={styles['personalisation__summary-item-value']}>
                    <div className={styles['personalisation__summary-item-value-text']}>
                      <p
                        className="body-sm font-medium"
                        dangerouslySetInnerHTML={{
                          __html: item.isSelected ? item.selectedValue?.text || item.selectedValue || '-' : '-'
                        }}
                      />
                      {item.isSelected && item.slotType !== 'CHECKBOX' && (
                        <p
                          className={`body-2xs ${item.price > 0 || (item.overridePrice > 0 && !item.isFreeSlot) ? styles['personalisation__summary-item-value-highlight'] : ''}`}
                        >
                          {item.slotType !== 'BASE_CONFIG' ? '+' : ''}
                          {item.overridePrice && !item.isFreeSlot
                            ? formatPrice({ amount: item.overridePrice })
                            : formatPrice({ amount: item.price })}
                        </p>
                      )}
                    </div>
                    {item.isSelected && (
                      <button
                        className={styles['personalisation__summary-item-edit'] + ' button-reset'}
                        onClick={() => handleEditClick(item.id)}
                        aria-label={`Edit ${item.title}`}
                      >
                        <span className={styles['personalisation__summary-item-edit-icon'] + ' svg-wrapper'}>
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M9.30298 1.75761C8.89292 1.34756 8.22812 1.34756 7.81806 1.75761L6.54277 3.03298C6.54122 3.03446 6.53969 3.03596 6.53817 3.03748C6.53664 3.03901 6.53514 3.04055 6.53365 3.04209L1.75773 7.81829C1.56083 8.01519 1.4502 8.28225 1.4502 8.56072V10.2501C1.4502 10.4158 1.58451 10.5501 1.7502 10.5501H3.43954C3.71799 10.5501 3.98507 10.4395 4.182 10.2426L10.2423 4.18188C10.6524 3.77182 10.6524 3.107 10.2423 2.69695M6.75036 3.67393L2.182 8.24255C2.09761 8.32695 2.0502 8.4414 2.0502 8.56072V9.95007H3.43954C3.5589 9.95007 3.67335 9.90267 3.75773 9.8183L8.3261 5.24966L6.75036 3.67393ZM8.75035 4.82539L9.81806 3.75762C9.9938 3.58188 9.9938 3.29696 9.81806 3.12122L8.87871 2.18188C8.70297 2.00614 8.41808 2.00614 8.24233 2.18187L7.17461 3.24966L8.75035 4.82539Z"
                              fill="#747D8A"
                            />
                          </svg>
                        </span>
                      </button>
                    )}
                  </div>
                </div>
                {item.isInvalid && (
                  <p className={`body-xs ${styles['personalisation__summary-item-error']}`}>
                    {item.invalidReason || 'This selection is no longer available.'}
                    {item.showRemoveOption && (
                      <>
                        {' or '}
                        <button
                          type="button"
                          className={styles['personalisation__summary-item-error-link'] + ' button-reset'}
                          onClick={() => handleRemoveSelection(item.id)}
                          aria-label={`Remove ${item.title} selection`}
                        >
                          remove it
                        </button>
                        .
                      </>
                    )}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
        <div className={styles['personalisation__summary-footer']}>
          <p className="body-base">Current Total:</p>
          <p className="body-lg font-medium">{formatPrice({ amount: currentTotalPrice })}</p>
        </div>
      </div>
    </PersonalisationHelperModal>
  );
}
